/**
 * Sidebar Component
 * 
 * Main navigation sidebar providing menu items for all application routes.
 * Displays user profile information, wallet balance, verification status,
 * and handles user logout. Adapts menu visibility based on user type.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '/styles/index.css';
import { LanguageSelector } from '@/shared/components';
import logoDark from '@/assets/images/logo-dark.png';
import logoLight from '@/assets/images/logo-light.png';
import { profileService, kycService, businessService } from '@/features/settings/services';
import { tokenService, authService } from '@/features/auth/services';
import { useTheme } from '@/app/providers/ThemeContext.jsx';
import { usePermissions } from '@/app/providers/PermissionsContext.jsx';
import { queryClient } from '@/app/config/react-query.js';
import { useHeaderProfile } from '@/shared/hooks/useHeaderProfile.js';
import logger from '@/shared/utils/logger.js';

const getMenuItems = (t) => [
  { name: t('dashboard', { ns: 'common' }), description: 'Overview', icon: 'dashboard', path: '/dashboard', permissionId: 'dashboard' },
  { name: t('disbursements', { ns: 'common' }), description: 'Send money', icon: 'south', path: '/disbursements', permissionId: 'disbursements' },
  { name: t('collections', { ns: 'common' }), description: 'Receive payments', icon: 'account_balance_wallet', path: '/collections', permissionId: 'collections' },
  { name: t('checkout', { ns: 'common', defaultValue: 'Checkout' }), description: 'Payment links', icon: 'shopping_cart', path: '/checkout', permissionId: 'checkout' },
  { name: t('utilities', { ns: 'common' }), description: 'Bills & top-ups', icon: 'bolt', path: '/utilities', permissionId: 'utilities' },
  { name: t('payroll', { ns: 'common' }), description: 'Staff payments', icon: 'group', path: '/payroll', permissionId: 'payroll' },
  { name: t('settlements', { ns: 'common', defaultValue: 'Settlements' }), description: 'Settlements', icon: 'savings', path: '/settlements', permissionId: 'settlements' },
// { name: t('sms', { ns: 'common', defaultValue: 'SMS' }), description: 'Messaging', icon: 'sms', path: '/SMS', permissionId: 'sms' },
  { name: t('reports', { ns: 'common' }), description: 'Analytics', icon: 'bar_chart', path: '/reports', permissionId: 'reports' },
  { name: t('settings', { ns: 'common' }), description: 'Preferences', icon: 'settings', path: '/settings', permissionId: 'settings' },
];

const Sidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { effectiveTheme } = useTheme();
  const { hasPermission, isInitialized } = usePermissions();
  const allMenuItems = getMenuItems(t);

  // Robust path matching for active state
  const isPathActive = (itemPath) => {
    const currentPath = location.pathname.toLowerCase();
    const targetPath = itemPath.toLowerCase();

    // Exact match or match with trailing slash
    if (currentPath === targetPath || currentPath === `${targetPath}/`) {
      return true;
    }

    // Sub-route match (e.g., /settings/profile matches /settings)
    // Avoid partial matches like /settings-other by checking for /
    if (targetPath !== '/' && currentPath.startsWith(`${targetPath}/`)) {
      return true;
    }

    return false;
  };

  // Get icon color based on current page
  const getIconColor = (itemPath) => {
    const pathColorMap = {
      '/dashboard': 'var(--icon-dashboard)',
      '/disbursements': 'var(--icon-disbursements)',
      '/collections': 'var(--icon-collections)',
      '/checkout': 'var(--icon-checkout, var(--accent))',
      '/utilities': 'var(--icon-utilities)',
      '/payroll': 'var(--icon-payroll)',
      '/settlements': 'var(--icon-settlements)',
      '/SMS': 'var(--icon-sms)',
      '/reports': 'var(--icon-reports)',
      '/settings': 'var(--icon-settings)'
    };

    return pathColorMap[itemPath] || 'var(--text-secondary)';
  };

  // Filter menu items based on permissions
  const menuItems = useMemo(() => {
    if (!isInitialized) {
      // While loading, show all items (will be filtered once loaded)
      return allMenuItems;
    }

    // Get user data to check user type
    const userData = tokenService.getUserData();
    const userType = (userData?.usertype || userData?.user_type || '').toLowerCase();

    const isAgent = userData?.business?.category === 'Agent' || 
                    userData?.businesses?.[0]?.category === 'Agent' ||
                    userData?.category === 'Agent' ||
                    userData?.business_type === 'agent' ||
                    userData?.business?.business_type === 'agent' ||
                    userData?.businesses?.[0]?.business_type === 'agent' ||
                    userData?.industry === 'Agent';

    let filteredMenuItems = allMenuItems;

    // Individual users have full access - show all menu items (unless they are an agent)
    if (userType === 'business_contact' || userType === 'business_member') {
      // For business_contact (business owners) and business_member (team members),
      // filter menu items based on permissions - hide items they don't have access to
      filteredMenuItems = allMenuItems.filter(item => {
        // Settings is always accessible (but sub-pages will be protected)
        if (item.path === '/settings') {
          return true;
        }
        // Only show menu items if user has permission
        return hasPermission(item.permissionId);
      });
    }

    // If the user is an Agent, restrict to specific allowed pages only
    if (isAgent) {
      const allowedAgentPaths = ['/dashboard', '/collections', '/disbursements', '/settings', '/reports'];
      filteredMenuItems = filteredMenuItems.filter(item => allowedAgentPaths.includes(item.path));
    }

    const filtered = filteredMenuItems;

    if (isInitialized && location.pathname === '/dashboard' && !filtered.some(i => i.path === '/dashboard')) {
      if (filtered.length > 0) {
        const firstPermitted = filtered[0].path;
        logger.debug(`No dashboard access. Redirecting to first permitted page: ${firstPermitted}`);
        navigate(firstPermitted, { replace: true });
      }
    }

    return filtered;
  }, [allMenuItems, hasPermission, isInitialized, location.pathname, navigate]);
  const [userData, setUserData] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isCompactMode, setIsCompactMode] = useState(false);

  // Use the same hook as Header for consistent verification API integration
  const {
    userData: profileUserData,
    businessVerification,
    verificationLevel,
    verificationInfo,
    getVerificationLevelColors,
  } = useHeaderProfile(); // Toggle between compact (Option 1) and expanded (Option 2)

  const currentDateLabel = useMemo(() => {
    const now = new Date();
    const weekday = now.getDay();
    const day = now.getDate();
    const month = now.getMonth();
    const year = now.getFullYear();

    const weekdays = [
      t('sunday', { ns: 'common' }),
      t('monday', { ns: 'common' }),
      t('tuesday', { ns: 'common' }),
      t('wednesday', { ns: 'common' }),
      t('thursday', { ns: 'common' }),
      t('friday', { ns: 'common' }),
      t('saturday', { ns: 'common' })
    ];

    const months = [
      t('january', { ns: 'common' }),
      t('february', { ns: 'common' }),
      t('march', { ns: 'common' }),
      t('april', { ns: 'common' }),
      t('may', { ns: 'common' }),
      t('june', { ns: 'common' }),
      t('july', { ns: 'common' }),
      t('august', { ns: 'common' }),
      t('september', { ns: 'common' }),
      t('october', { ns: 'common' }),
      t('november', { ns: 'common' }),
      t('december', { ns: 'common' })
    ];

    return `${weekdays[weekday]}, ${day} ${months[month]} ${year}`;
  }, [t]);

  const logoSrc = effectiveTheme === 'dark' ? logoDark : logoLight;

  // Load user data and wallet balance from localStorage
  useEffect(() => {
    const existingUserData = tokenService.getUserData();
    const existingWalletBalance = tokenService.getWalletBalance();

    if (existingUserData) {
      setUserData(existingUserData);
    }

    if (existingWalletBalance !== undefined) {
      setWalletBalance(existingWalletBalance);
    }

    // Listen for storage changes (when user logs in/out)
    const handleStorageChange = () => {
      const userData = tokenService.getUserData();
      const balance = tokenService.getWalletBalance();
      if (userData) { setUserData(userData); }
      if (balance !== undefined) { setWalletBalance(balance); }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Handle window resize for responsive design
  useEffect(() => {
    let resizeTimer = null;
    const handleResize = () => {
      // Debounce resize events to prevent excessive re-renders
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
      resizeTimer = setTimeout(() => {
        setWindowWidth(window.innerWidth);
      }, 100); // 100ms debounce
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
    };
  }, []);

  const getVerificationBadgeClass = () => {
    if (userData?.usertype?.toLowerCase() === 'business_contact') {
      if (!businessVerification) { return 'verification-badge loading'; }
      return businessVerification.status === 'verified'
        ? 'verification-badge verified'
        : 'verification-badge pending';
    }
    if (verificationLevel === null) { return 'verification-badge loading'; }
    return `verification-badge level-${verificationLevel}`;
  };

  const handleUpgradeClick = () => {
    if (userData?.usertype?.toLowerCase() === 'business_contact') {
      navigate('/settings/business');
    } else {
      navigate('/settings/verification');
    }
  };

  // Get user's first name or fallback
  const getUserFirstName = () => {
    if (userData) {
      const userType = userData.usertype?.toLowerCase() || userData.user_type?.toLowerCase();
      const isBusiness = userType === 'business_contact' || userType === 'business_member' || userType === 'business';
      if (isBusiness) {
        const bizName = userData.business?.trading_name ||
          userData.business?.business_name ||
          userData.business_name ||
          userData.trading_name ||
          userData.businesses?.[0]?.trading_name ||
          userData.businesses?.[0]?.business_name;
        if (bizName) { return bizName; }
      }

      // Check for first_name (individual users from API response)
      if (userData.first_name) {
        return userData.first_name;
      }

      // Check for full_name (business users from API response)
      if (userData.full_name) {
        return userData.full_name.split(' ')[0];
      }

      // Check for name field
      if (userData.name) {
        return userData.name.split(' ')[0];
      }

      // Check for firstName field (camelCase)
      if (userData.firstName) {
        return userData.firstName;
      }

      // Fallback to email username
      if (userData.email) {
        return userData.email.split('@')[0];
      }
    }
    return t('user', { ns: 'common', defaultValue: 'User' });
  };

  // Get user's full name for display
  const getUserFullName = () => {
    if (userData) {
      const userType = userData.usertype?.toLowerCase() || userData.user_type?.toLowerCase();
      const isBusiness = userType === 'business_contact' || userType === 'business_member' || userType === 'business';
      if (isBusiness) {
        const bizName = userData.business?.trading_name ||
          userData.business?.business_name ||
          userData.business_name ||
          userData.trading_name ||
          userData.businesses?.[0]?.trading_name ||
          userData.businesses?.[0]?.business_name;
        if (bizName) { return bizName; }
      }

      // For individual users, combine first and last name
      if (userData.first_name && userData.last_name) {
        return `${userData.first_name} ${userData.last_name}`;
      }

      // For business users, use full_name
      if (userData.full_name) {
        return userData.full_name;
      }

      // Fallback to first name only
      if (userData.first_name) {
        return userData.first_name;
      }

      // Fallback to email
      if (userData.email) {
        return userData.email.split('@')[0];
      }
    }
    return t('user', { ns: 'common', defaultValue: 'User' });
  };

  // Get verification status text - now uses verificationInfo from hook
  const getVerificationStatus = () => {
    const level = userData?.usertype?.toLowerCase() === 'business_contact'
      ? (businessVerification?.level ?? 0)
      : (verificationLevel ?? 0);
    return `verification lv ${level}`;
  };

  // Get verification status color
  const getVerificationStatusColor = () => {
    if (userData?.usertype?.toLowerCase() === 'business_contact') {
      if (!businessVerification) { return 'var(--text-muted)'; }
      return businessVerification.status === 'verified' ? 'var(--success)' : 'var(--warning)';
    }
    // For individual users
    if (verificationLevel === null) { return 'var(--text-muted)'; }
    const colors = getVerificationLevelColors(verificationLevel);
    return colors.color;
  };

  const getVerificationDescription = () => {
    if (userData?.usertype?.toLowerCase() === 'business_contact') {
      if (!businessVerification) { return 'Complete your business verification to access all features'; }
      return businessVerification.status === 'verified'
        ? 'Your business is fully verified with all features'
        : 'Complete your business verification to access all features';
    }
    if (verificationLevel === null) { return 'Loading verification details...'; }
    const descriptions = [
      'Basic account with limited features',
      'Email and phone verification completed',
      'ID verification in progress',
      'Fully verified account with all features'
    ];
    return descriptions[verificationLevel] || 'Verification status unknown';
  };

  // Check if mobile
  const isMobile = windowWidth <= 768;

  const handleLogout = async () => {
    try {
      // Clean up sidebar overlay immediately to prevent blur effect on logout
      const sidebar = document.querySelector('.sidebar');
      const overlay = document.querySelector('.sidebar-overlay');

      if (sidebar) {
        sidebar.classList.remove('open');
        // Clear inline styles immediately and remove blur
        sidebar.style.transition = '';
        sidebar.style.left = '';
        sidebar.style.visibility = '';
        sidebar.style.backdropFilter = 'none';
        sidebar.style.webkitBackdropFilter = 'none';
      }

      if (overlay) {
        overlay.classList.remove('active');
        // Remove overlay immediately without delay
        overlay.style.transition = 'none';
        overlay.style.opacity = '0';
        overlay.style.visibility = 'hidden';
        overlay.style.pointerEvents = 'none';
        overlay.style.background = 'transparent';
        overlay.style.backdropFilter = 'none';
        overlay.style.webkitBackdropFilter = 'none';
        if (overlay.parentNode) {
          overlay.remove();
        }
      }

      // Also ensure body and main content don't have any lingering blur effects
      const body = document.body;
      const dashboardMain = document.querySelector('.dashboard-main');
      const dashboardContent = document.querySelector('.dashboard-content');

      if (body) {
        body.style.backdropFilter = '';
        body.style.webkitBackdropFilter = '';
        body.style.filter = '';
      }

      if (dashboardMain) {
        dashboardMain.style.backdropFilter = '';
        dashboardMain.style.webkitBackdropFilter = '';
        dashboardMain.style.filter = '';
      }

      if (dashboardContent) {
        dashboardContent.style.backdropFilter = '';
        dashboardContent.style.webkitBackdropFilter = '';
        dashboardContent.style.filter = '';
      }

      // Set logout flag immediately to prevent any new API calls
      localStorage.setItem('isLoggingOut', 'true');
      localStorage.setItem('isAuthenticated', 'false');

      logger.debug('Logout initiated - preventing all API calls');

      // Cancel all React Query queries to prevent refetching
      try {
        if (queryClient) {
          queryClient.cancelQueries();
          queryClient.clear();
          logger.debug('All React Query queries cancelled');
        }
      } catch (queryError) {
        logger.warn('Could not cancel React Query', { error: queryError });
      }

      // Call logout API
      logger.debug('Calling logout API');
      const result = await tokenService.safeLogout();

      logger.debug('Logout API result', { result, response: result.response });

      // All session data is cleared by tokenService.safeLogout()
      // Navigate to login page immediately
      logger.debug('Logout complete, navigating to login');
      navigate('/login', { replace: true });
    } catch (error) {
      logger.error('Error during logout', { error });

      // Ensure overlay is cleaned up even on error
      const overlay = document.querySelector('.sidebar-overlay');
      if (overlay && overlay.parentNode) {
        overlay.remove();
      }

      // Clear session data even if there's an error
      try {
        tokenService.clearAuthData();
      } catch (clearError) {
        logger.error('Failed to clear auth data', { error: clearError });
      }
      // Still navigate to login even if there was an error
      navigate('/login', { replace: true });
    }
  };

  // Close sidebar when route changes - instant close like desktop
  useEffect(() => {
    if (window.innerWidth <= 768) {
      const sidebar = document.querySelector('.sidebar');
      const overlay = document.querySelector('.sidebar-overlay');

      if (sidebar && sidebar.classList.contains('open')) {
        // Remove open class first
        sidebar.classList.remove('open');

        // Clean up inline styles after a brief delay to allow CSS to take over
        setTimeout(() => {
          if (sidebar) {
            sidebar.style.transition = '';
            sidebar.style.left = '';
            sidebar.style.visibility = '';
          }
        }, 150);

        if (overlay) {
          overlay.classList.remove('active');
          // Clean up overlay inline styles
          setTimeout(() => {
            if (overlay && overlay.parentNode) {
              overlay.style.transition = '';
              overlay.style.opacity = '';
              overlay.style.visibility = '';
              overlay.remove();
            }
          }, 150);
        }
      }
    }
  }, [location.pathname]);

  const closeSidebarOnMobile = () => {
    // Close sidebar on mobile
    if (window.innerWidth <= 768) {
      const sidebar = document.querySelector('.sidebar');
      const overlay = document.querySelector('.sidebar-overlay');

      if (sidebar && sidebar.classList.contains('open')) {
        // Remove open class - CSS will handle the transition
        sidebar.classList.remove('open');

        // Clean up inline styles after transition completes
        setTimeout(() => {
          if (sidebar) {
            sidebar.style.transition = '';
            sidebar.style.left = '';
            sidebar.style.visibility = '';
          }
        }, 150);

        // Hide overlay
        if (overlay) {
          overlay.classList.remove('active');
          setTimeout(() => {
            if (overlay && overlay.parentNode) {
              overlay.style.transition = '';
              overlay.style.opacity = '';
              overlay.style.visibility = '';
              overlay.remove();
            }
          }, 150);
        }
      }
    }
  };

  return (
    <nav className={`sidebar ${isCompactMode ? 'compact' : ''}`}>
      <div className="sidebar-top-wrapper">
        <div className="sidebar-header">
          <div className="logo-container">
            <img
              src={logoSrc}
              alt="iSmartPay Logo"
              className="logo-image"
              style={{
                transition: 'opacity var(--transition-theme)',
                opacity: 1
              }}
            />
          </div>
        </div>

        <div className="sidebar-date">
          <span>{currentDateLabel}</span>
        </div>
      </div>

      <div style={{
        borderTop: '1px solid var(--border-primary)',
        margin: '0px'
      }}></div>

      <ul
        className="sidebar-menu"
        role="menubar"
        aria-label={t('mainNavigation', { ns: 'common', defaultValue: 'Main navigation' })}
      >
        {menuItems.map((item, idx) => (
          <li
            key={idx}
            className={`menu-item ${isPathActive(item.path) ? 'active' : ''}`}
            role="none"
          >
            <Link
              to={item.path}
              role="menuitem"
              tabIndex={idx === 0 ? 0 : -1}
              onClick={(e) => {
                // Prevent default to ensure we control navigation
                e.preventDefault();

                // Navigate first
                navigate(item.path);

                // Then close sidebar on mobile after a small delay to ensure navigation happens
                if (window.innerWidth <= 768) {
                  // Use requestAnimationFrame to ensure navigation completes first
                  requestAnimationFrame(() => {
                    const sidebar = document.querySelector('.sidebar');
                    const overlay = document.querySelector('.sidebar-overlay');

                    if (sidebar && sidebar.classList.contains('open')) {
                      sidebar.classList.remove('open');
                      // Clean up inline styles
                      setTimeout(() => {
                        if (sidebar) {
                          sidebar.style.transition = '';
                          sidebar.style.left = '';
                          sidebar.style.visibility = '';
                        }
                      }, 150);
                    }

                    if (overlay) {
                      overlay.classList.remove('active');
                      setTimeout(() => {
                        if (overlay && overlay.parentNode) {
                          overlay.style.transition = '';
                          overlay.style.opacity = '';
                          overlay.style.visibility = '';
                          overlay.remove();
                        }
                      }, 150);
                    }
                  });
                }
              }}
              onKeyDown={(e) => {
                const menuItems = Array.from(e.currentTarget.closest('ul')?.querySelectorAll('[role="menuitem"]') || []);
                const currentIndex = menuItems.indexOf(e.currentTarget);

                // Handle arrow key navigation
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  e.stopPropagation();

                  let nextIndex;
                  if (e.key === 'ArrowDown') {
                    nextIndex = currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0;
                  } else {
                    nextIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1;
                  }

                  const nextItem = menuItems[nextIndex];
                  if (nextItem) {
                    // Remove tabIndex from all items
                    menuItems.forEach(item => item.setAttribute('tabIndex', '-1'));
                    // Set tabIndex to 0 for the next item and focus it
                    nextItem.setAttribute('tabIndex', '0');
                    nextItem.focus();
                  }
                } else if (e.key === 'Home') {
                  e.preventDefault();
                  e.stopPropagation();
                  const firstItem = menuItems[0];
                  if (firstItem) {
                    menuItems.forEach(item => item.setAttribute('tabIndex', '-1'));
                    firstItem.setAttribute('tabIndex', '0');
                    firstItem.focus();
                  }
                } else if (e.key === 'End') {
                  e.preventDefault();
                  e.stopPropagation();
                  const lastItem = menuItems[menuItems.length - 1];
                  if (lastItem) {
                    menuItems.forEach(item => item.setAttribute('tabIndex', '-1'));
                    lastItem.setAttribute('tabIndex', '0');
                    lastItem.focus();
                  }
                }
              }}
              onFocus={(e) => {
                // When an item receives focus, make it the only tabbable item
                const menuItems = Array.from(e.currentTarget.closest('ul')?.querySelectorAll('[role="menuitem"]') || []);
                menuItems.forEach(item => item.setAttribute('tabIndex', '-1'));
                e.currentTarget.setAttribute('tabIndex', '0');
              }}
              aria-label={isCompactMode ? `${item.name}: ${item.description}` : item.name}
              data-tooltip={item.name}
            >
              <span
                className="material-symbols-outlined icon"
                style={{
                  color: (() => {
                    const isActive = isPathActive(item.path);
                    const color = isActive ? getIconColor(item.path) : 'var(--text-secondary)';
                    return color;
                  })(),
                  transition: 'color var(--transition-base)'
                }}
              >{item.icon}</span>
              {!isCompactMode && (
                <div className="menu-text-group">
                  <span className="menu-title">{item.name}</span>
                  <span className="menu-description">{item.description}</span>
                </div>
              )}
            </Link>
          </li>
        ))}
      </ul>

      <div className="sidebar-footer">
        <div className="user-info-section" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '12px' }}>
          <button
            className="profile-button"
            onClick={() => {
              navigate('/settings');
              closeSidebarOnMobile();
            }}
            aria-label={`${t('settings', { ns: 'common' })} - ${getUserFirstName()}`}
            title={`${t('settings', { ns: 'common' })} - ${getUserFirstName()}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0px',
              cursor: 'pointer',
              flex: 1,
              padding: '8px 12px',
              backgroundColor: 'var(--bg-tertiary)',
              border: '1px solid var(--border-primary)',
              borderRadius: '8px',
              transition: 'all 0.2s ease',
              height: '56px',
              minWidth: 0,
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
              e.currentTarget.style.borderColor = 'var(--accent)';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = 'var(--shadow-md)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
              e.currentTarget.style.borderColor = 'var(--border-primary)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div className="user-text-group" style={{
              minWidth: 0,
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1px',
              padding: '2px 0'
            }}>
              <span className="user-name" style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                fontSize: '13px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                textAlign: 'center'
              }}>
                {getUserFullName()}
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div
                  className="verification-badge"
                  role="button"
                  tabIndex={0}
                  style={(() => {
                    const level = userData?.usertype?.toLowerCase() === 'business_contact'
                      ? (businessVerification?.level ?? 0)
                      : (verificationLevel ?? 0);
                    const colors = getVerificationLevelColors(level);
                    return {
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '3px',
                      fontSize: 'clamp(8px, 1.5vw, 9px)', // Smaller responsive font
                      color: colors.text,
                      fontWeight: '600',
                      padding: '3px 6px', // Reduced padding
                      borderRadius: '6px',
                      background: colors.background,
                      border: `1px solid ${colors.border}`,
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      width: 'auto',
                      maxWidth: '100%',
                      margin: '0 auto',
                      boxShadow: colors.shadow || '0 2px 6px rgba(15, 23, 42, 0.06)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      minHeight: '20px',
                      fontFamily: 'inherit'
                    };
                  })()}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering parent button
                    navigate(userData?.usertype?.toLowerCase() === 'business_contact'
                      ? '/settings/business/verification'
                      : '/settings/individual/verification');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(userData?.usertype?.toLowerCase() === 'business_contact'
                        ? '/settings/business/verification'
                        : '/settings/individual/verification');
                    }
                  }}
                  aria-label={`${t('viewVerificationStatus', { ns: 'common', defaultValue: 'View verification status' })}: ${getVerificationStatus()}`}
                  title={`${t('verificationStatus', { ns: 'settings' })}: ${getVerificationStatus()}`}
                >
                  <span className="material-symbols-outlined" style={(() => {
                    const level = userData?.usertype?.toLowerCase() === 'business_contact'
                      ? (businessVerification?.level ?? 0)
                      : (verificationLevel ?? 0);
                    const colors = getVerificationLevelColors(level);
                    return {
                      fontSize: 'clamp(9px, 1.5vw, 11px)', // Smaller responsive icon
                      color: colors.icon ?? colors.text,
                      background: colors.iconBackground || (effectiveTheme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.25)'),
                      padding: '2px',
                      borderRadius: '999px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: effectiveTheme === 'dark'
                        ? 'inset 0 0 0 1px rgba(255,255,255,0.2)'
                        : 'inset 0 0 0 1px rgba(255,255,255,0.3)'
                    };
                  })()}>
                    {(() => {
                      const level = userData?.usertype?.toLowerCase() === 'business_contact'
                        ? (businessVerification?.level ?? 0)
                        : (verificationLevel ?? 0);
                      return level >= 3 ? 'verified' : level >= 2 ? 'verified' : level === 1 ? 'pending' : 'error';
                    })()}
                  </span>
                  {getVerificationStatus()}
                </div>
              </div>
            </div>
          </button>
          <button
            className="logout-button"
            onClick={handleLogout}
            aria-label={t('logout', { ns: 'common' })}
            title={t('logout', { ns: 'common' })}
            style={{
              padding: '10px 12px',
              backgroundColor: 'var(--error-bg)',
              border: '1px solid var(--error)',
              borderRadius: '8px',
              color: 'var(--error)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              minWidth: '56px',
              height: '56px',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--error)';
              e.currentTarget.style.borderColor = 'var(--error)';
              e.currentTarget.style.color = 'var(--text-inverse)';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = 'var(--shadow-md)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--error-bg)';
              e.currentTarget.style.borderColor = 'var(--error)';
              e.currentTarget.style.color = 'var(--error)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '22px', color: 'inherit' }}>
              logout
            </span>
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Sidebar;